import styles from './TableRow.module.scss';

import clsx from 'clsx';
import produce from 'immer';
import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TableCommonProps } from 'react-table';

import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { reactRefSetter } from '@work4all/utils';

import { useInifiteList } from '../../InfiniteList';
import { EditTablePositionKind } from '../../types';
import { useEditTable } from '../edit-table-provider/EditTableProvider';
import { TableCell } from '../TableCell/TableCell';

import {
  DRAG_HANDLER_COLUMN_WIDTH,
  TABLE_ROW_DND_ITEM_TYPE,
} from './constants';
import { DragHandlerIconButton } from './DragHandlerIconButton';
import { ResizableContext, useResizable } from './use-resizable';

type ITableRowDndItem = {
  id: string;
  index: number;
};

export type ITableRowProps = TableCommonProps & {
  disabled?: boolean;
  localId: string;
  index: number;
  onMoveRow?: (localId: string, index: number) => void;
  children?: ReactNode;
  kind?: EditTablePositionKind;
  onClickOutside?: (localId: string) => void;
  isNormal?: boolean;
  canDrop: (input: { id: string; index: number }) => number;
  hasBorder?: {
    vertical: boolean;
    horizontal: boolean;
  };
  isSimpleMode?: boolean;
  isMenuVisible?: boolean;
  onClick?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >['onClick'];
};
let dndItemRef = undefined;

const paddingsOfParents = remToPx(0.5);

const TableRow = (
  props: ITableRowProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const {
    disabled = false,
    localId,
    index,
    onMoveRow: moveRowDone,
    className,
    style,
    children,
    kind,
    canDrop,
    onClickOutside,
    isNormal = true,
    hasBorder = { vertical: false, horizontal: false },
    isSimpleMode,
    isMenuVisible,
    ...rest
  } = props;

  const dropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  const [{ isOver, direction }, drop] = useDrop({
    accept: TABLE_ROW_DND_ITEM_TYPE,
    canDrop: (item, monitor) => {
      const canDropValue = canDrop(monitor.getItem());
      return Boolean(canDropValue);
    },
    collect: (monitor) => {
      const canDropValue = canDrop(monitor.getItem());
      return {
        isOver: monitor.isOver() && canDropValue,
        direction: canDropValue > 0 ? 'up' : 'down',
      };
    },
    hover(item: ITableRowDndItem) {
      if (!dropRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      dndItemRef = { id: item.id, index: hoverIndex };
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    ITableRowDndItem,
    unknown,
    { isDragging: boolean }
  >({
    type: TABLE_ROW_DND_ITEM_TYPE,
    item: { id: localId, index },
    options: {
      dropEffect: 'move',
    },
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    end() {
      if (dndItemRef) {
        moveRowDone?.(dndItemRef.id, dndItemRef.index);
      }
    },
  });

  preview(drop(dropRef));
  drag(dragRef);

  const modifiedStyle = produce(style, (draft) => {
    draft.width =
      (typeof draft.width === 'number' ? draft.width : parseInt(draft.width)) +
      DRAG_HANDLER_COLUMN_WIDTH;
    draft.opacity = isDragging ? 0 : 1;
    draft.height =
      kind === ErpPositionsKind.SEITENUMBRUCH &&
      typeof draft.height === 'number'
        ? draft.height + 1
        : draft.height;
  });

  const orientation = direction === 'up' ? '0 4px 0px 0px' : '0 -4px 0px 0px';
  const overStyle: React.CSSProperties = isOver
    ? {
        boxShadow: orientation + ' inset var(--brand01)',
      }
    : {};

  const horizontalWidth =
    kind === ErpPositionsKind.SEITENUMBRUCH ? 2 : hasBorder?.horizontal ? 1 : 0;
  const verticalWidth =
    kind === ErpPositionsKind.SEITENUMBRUCH ? 2 : hasBorder?.vertical ? 1 : 0;

  const border: React.CSSProperties = {
    borderBottomStyle: 'solid',
    borderColor: 'var(--ui04)',
    borderTopWidth: horizontalWidth,
    borderBottomWidth: horizontalWidth,
    borderLeftWidth: verticalWidth,
    borderRightWidth: verticalWidth,
    marginTop: kind === ErpPositionsKind.SEITENUMBRUCH ? -1 : 0,
    width: isSimpleMode ? 'fit-content' : '100%',
    ...overStyle,
  };

  const elId = `position_row_${localId}`;

  useEffect(() => {
    const onClick = (evt) => {
      const flyoutEl = document.getElementById(`${elId}`);
      let targetEl = evt.target as HTMLElement; // clicked element

      do {
        if (targetEl === flyoutEl || targetEl.id === elId) {
          return;
        }
        targetEl = targetEl.parentElement;
      } while (targetEl && targetEl?.nodeName !== 'BODY');
      if (targetEl?.nodeName === 'BODY') onClickOutside?.(localId);
    };

    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [localId, elId, onClickOutside]);

  const { onAddPosition } = useEditTable();
  const handleAdd = (offset = 0) => {
    onAddPosition({
      positionType: ErpPositionsKind.TEXTZEILE,
      article: { id: 0 },
      index: index + offset,
      cacheOnly: true,
    });
  };

  const resizable = useResizable();
  const {
    handleElement,
    resizableRef,
    style: resizableStyle,
    height,
  } = resizable;
  const { measureMax } = useInifiteList();

  useEffect(() => {
    // remove static offset of paddings
    if (height) measureMax(index, height - paddingsOfParents);
  }, [index, measureMax, height]);

  const resizableValue = useMemo(() => {
    const setHeight = (e: number | undefined) => {
      if (e === undefined) {
        measureMax(index, undefined);
      }
      resizable.setHeight(e);
    };
    return {
      ...resizable,
      setHeight,
    };
  }, [resizable, measureMax]);

  return (
    <ResizableContext.Provider value={resizableValue}>
      <div
        id={elId}
        key={elId}
        ref={reactRefSetter(forwardedRef, dropRef, resizableRef)}
        className={clsx(styles['table-row'], className, {
          [styles['borderRight']]: hasBorder?.vertical,
        })}
        style={{
          ...modifiedStyle,
          ...border,
          ...resizableStyle,
          minHeight: 'calc(2.25rem + 2px)',
        }}
        {...rest}
      >
        {!isMenuVisible ? null : disabled || !moveRowDone ? (
          <TableCell className={styles['drag-empty']} />
        ) : (
          <TableCell
            ref={dragRef}
            role="cell"
            className={clsx(styles['drag-handler'], {
              [styles['drag-handler-simple']]: isSimpleMode,
            })}
            kind={kind}
          >
            <DragHandlerIconButton
              onAddAfter={
                isNormal
                  ? () => {
                      handleAdd(1);
                    }
                  : undefined
              }
              onAddBefore={
                isNormal
                  ? () => {
                      handleAdd();
                    }
                  : undefined
              }
            />
          </TableCell>
        )}

        {children}
        {handleElement}
      </div>
    </ResizableContext.Provider>
  );
};

const ForwardRefTableRow = forwardRef(TableRow);

export { ForwardRefTableRow as TableRow };
